import { encodeTelemetryEvent, type TelemetryEvent, type TelemetrySurface, type WireEvent } from '@duck/api-definitions'
import { ClientAnalytics } from '@/modules/clientAnalytics'
import { getCurrentAccessToken } from '@/modules/apiClients'

type Send = (challengeId: string, body: { b: WireEvent[] }) => Promise<void>

const PREVIEW_MAX = 200
const INJECT_MIN_LEN = 50
const KEYDOWN_GRACE_MS = 100
// A typing stretch closes when the keyboard has been quiet this long, or on any other action.
const TYPING_IDLE_MS = 30_000
const COPY_RING_SIZE = 50
const MAX_FAILURES = 3
const DEFAULT_FLUSH_MS = 10_000
const REPLACED_ALL_RATIO = 0.9
const MAX_BATCH = 500 // server rejects larger batches
const KEEPALIVE_MAX_BYTES = 60_000 // browsers reject keepalive bodies over 64 KiB

type Surface = { surface: TelemetrySurface; element: HTMLElement; getText: () => string }

// Module state: one recorder per page, as the workspace mounts once.
const surfaces = new Set<Surface>()
let queue: TelemetryEvent[] = []
let lastInputAt = 0
let lastKeydownAt = 0
let stretch: { start: number; last: number; keystrokes: number; surface: TelemetrySurface } | null = null
let awayAt: number | null = null
let failures = 0
let reportedFailure = false
const inflight = new Set<Promise<void>>()
let sendImpl: Send | null = null
let activeChallengeId: string | null = null
let pendingInsertText: string | null = null
const copyKeys: string[] = []

class SyncError extends Error {
  constructor(readonly status: number) { super(`sync ${status}`) }
}

const normalize = (s: string) => s.replace(/\s+/g, ' ').trim()
// Two FNV-1a 32-bit passes with different seeds give a 64-bit key: 50 entries at 2^-64 collision odds each,
// and nothing readable held in memory.
const fnv1a = (s: string, seed: number): number => {
  let h = seed
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h
}
const copyKey = (text: string) => { const n = normalize(text); return `${fnv1a(n, 0x811c9dc5)}:${fnv1a(n, 0x050c5d1f)}` }
const rememberCopy = (text: string) => {
  if (text.length === 0) return
  copyKeys.push(copyKey(text))
  if (copyKeys.length > COPY_RING_SIZE) copyKeys.shift()
}
const isInternal = (text: string) => copyKeys.includes(copyKey(text))

const surfaceFor = (target: EventTarget | null): Surface | undefined => {
  if (!(target instanceof Node)) return undefined
  for (const s of surfaces) if (s.element.contains(target)) return s
  return undefined
}

const push = (e: TelemetryEvent) => { queue.push(e) }

// One 'active' event per typing stretch: when it started, how long it ran, how many keystrokes (inserts and
// deletes alike). A stretch ends on any other action (paste, drop, inject, run, leaving the tab), a change of
// surface, or TYPING_IDLE_MS without a key.
const closeStretch = () => {
  if (stretch === null) return
  push({ t: stretch.start, type: 'active', surface: stretch.surface, len: stretch.keystrokes, durationMs: stretch.last - stretch.start })
  stretch = null
}
const closeStretchIfIdle = (now: number) => {
  if (stretch !== null && now - stretch.last >= TYPING_IDLE_MS) closeStretch()
}
const keystroke = (now: number, surface: TelemetrySurface) => {
  if (stretch !== null && (stretch.surface !== surface || now - stretch.last >= TYPING_IDLE_MS)) closeStretch()
  if (stretch === null) stretch = { start: now, last: now, keystrokes: 0, surface }
  stretch.keystrokes += 1
  stretch.last = now
}

const recordInsert = (type: 'paste' | 'drop', s: Surface, text: string) => {
  if (text.length === 0) return
  closeStretch()
  const now = Date.now()
  const base: TelemetryEvent = {
    t: now,
    type,
    surface: s.surface,
    len: text.length,
    lines: text.split('\n').length,
    internal: isInternal(text),
    preview: text.slice(0, PREVIEW_MAX),
    pauseMs: lastInputAt === 0 ? 0 : now - lastInputAt,
  }
  lastInputAt = now
  // Editors that apply the paste via execCommand('insertText') fire a second beforeinput with the same text.
  pendingInsertText = text
  // The editor applies the paste in its own (bubble-phase) handler after this capture-phase one, and a microtask
  // would run between the two listeners; a zero-delay timeout fires once the editor has applied it.
  // Bind to the queue that is live now: if the workspace unmounts and the next one starts before the timeout
  // fires, the event still belongs to this challenge (stop's follow-up flush drains that same array).
  const q = queue
  window.setTimeout(() => {
    pendingInsertText = null
    const total = s.getText().length
    base.replacedAll = total > 0 && text.length / total >= REPLACED_ALL_RATIO
    q.push(base)
  }, 0)
}

const onPaste = (ev: Event) => {
  const s = surfaceFor(ev.target)
  if (s === undefined) return
  const text = (ev as ClipboardEvent).clipboardData?.getData('text/plain') ?? ''
  recordInsert('paste', s, text)
}
const onDrop = (ev: Event) => {
  const s = surfaceFor(ev.target)
  if (s === undefined) return
  const text = (ev as DragEvent).dataTransfer?.getData('text/plain') ?? ''
  recordInsert('drop', s, text)
}
// Any in-app copy, cut or drag counts (task body, schema, results grid). Bubble phase so editors that write
// clipboardData themselves (Monaco, CodeMirror) have done so by the time we read it. Drag events carry no
// clipboardData and fall through to the selection, so a drag-reorder inside an editor reads as internal.
const onCopy = (ev: Event) => {
  const data = (ev as ClipboardEvent).clipboardData?.getData('text/plain') ?? ''
  rememberCopy(data !== '' ? data : window.getSelection()?.toString() ?? '')
}
const onKeydown = () => { lastKeydownAt = Date.now() }
const onBeforeInput = (ev: Event) => {
  const s = surfaceFor(ev.target)
  if (s === undefined) return
  const { inputType, data } = ev as InputEvent
  if (inputType === 'insertFromPaste' || inputType === 'insertFromDrop') return // counted by paste/drop handlers
  if (inputType === 'insertText' && data === pendingInsertText) { pendingInsertText = null; return } // same paste, re-applied by the editor
  if (!inputType.startsWith('insert') && !inputType.startsWith('delete')) return
  const now = Date.now()
  const len = data?.length ?? 0
  const keyed = now - lastKeydownAt <= KEYDOWN_GRACE_MS
  if ((inputType === 'insertText' || inputType === 'insertReplacementText') && len > INJECT_MIN_LEN && !keyed) {
    closeStretch()
    push({ t: now, type: 'inject', surface: s.surface, len, preview: (data ?? '').slice(0, PREVIEW_MAX), pauseMs: lastInputAt === 0 ? 0 : now - lastInputAt })
    lastInputAt = now
    return
  }
  keystroke(now, s.surface)
  lastInputAt = now
}
const goAway = () => {
  if (awayAt !== null) return
  closeStretch()
  awayAt = Date.now()
  push({ t: awayAt, type: 'blur' })
  void flush()
}
const comeBack = () => {
  if (awayAt === null) return
  const now = Date.now()
  push({ t: now, type: 'focus', awayMs: now - awayAt })
  awayAt = null
}
const onVisibility = () => { if (document.visibilityState === 'hidden') goAway(); else comeBack() }

// Flushes run concurrently: each takes its own slice of the queue, so a flush during an in-flight one
// (blur then pagehide, stop then its 0ms follow-up) still leaves the page with every event sent.
// `q` defaults to the live queue; stop passes the queue it owned so a later start cannot mix challenges.
function flush(send: Send | null = sendImpl, challengeId: string | null = activeChallengeId, q: TelemetryEvent[] = queue): Promise<void> {
  if (send === null || challengeId === null) return Promise.resolve()
  closeStretchIfIdle(Date.now())
  if (q.length === 0) return Promise.resolve()
  const batch = q.splice(0, MAX_BATCH)
  const p = send(challengeId, { b: batch.map(encodeTelemetryEvent) })
    .then(() => { failures = 0 })
    .catch((err: unknown) => {
      const status = err instanceof SyncError ? err.status : 0
      // Expired token (next axios call refreshes it) or rate limit: keep the batch, do not count a failure.
      if (status === 401 || status === 429) { q.unshift(...batch); return }
      // Any other 4xx (challenge finished, bad batch): nothing to retry.
      if (status >= 400 && status < 500) return
      failures += 1
      if (failures < MAX_FAILURES) { q.unshift(...batch); return }
      failures = 0
      if (reportedFailure) return
      reportedFailure = true
      ClientAnalytics.track('workspace:telemetry:failed', { challengeId, dropped: batch.length })
    })
    .finally(() => { inflight.delete(p) })
  inflight.add(p)
  return p
}

// The access token lives in memory (not a cookie), so attach it ourselves; no token → throw so the batch is retried.
const defaultSend: Send = async (challengeId, body) => {
  const token = getCurrentAccessToken()
  if (token === null || token === '') throw new Error('sync: no access token')
  const json = JSON.stringify(body)
  const res = await fetch(`/api/challenges/${encodeURIComponent(challengeId)}/sync`, {
    method: 'POST',
    keepalive: json.length <= KEEPALIVE_MAX_BYTES,
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: json,
  })
  if (!res.ok) throw new SyncError(res.status)
}

/** Closes the open typing stretch and sends everything queued. Call before submit. */
export async function flushTelemetry(): Promise<void> {
  try {
    closeStretch()
    // A failed in-flight flush re-queues its batch; wait so that batch is included below.
    await Promise.all([...inflight])
    await flush()
  } catch {
    // Telemetry must never block a submit.
  }
}

export function registerTelemetrySurface(surface: TelemetrySurface, element: HTMLElement, getText: () => string): () => void {
  const entry: Surface = { surface, element, getText }
  surfaces.add(entry)
  return () => { surfaces.delete(entry) }
}

export function recordTelemetryRun(input: { surface: TelemetrySurface; len: number; error?: string }): void {
  closeStretch()
  const e: TelemetryEvent = { t: Date.now(), type: 'run', surface: input.surface, len: input.len }
  if (input.error !== undefined) e.error = input.error
  push(e)
}

export function startWorkspaceTelemetry(challengeId: string, options: { flushIntervalMs?: number; send?: Send } = {}): () => void {
  activeChallengeId = challengeId
  sendImpl = options.send ?? defaultSend
  queue = []
  lastInputAt = 0
  lastKeydownAt = 0
  stretch = null
  awayAt = null
  failures = 0
  reportedFailure = false
  pendingInsertText = null

  document.addEventListener('paste', onPaste, true)
  document.addEventListener('drop', onDrop, true)
  document.addEventListener('copy', onCopy)
  document.addEventListener('cut', onCopy)
  document.addEventListener('dragstart', onCopy)
  document.addEventListener('keydown', onKeydown, true)
  document.addEventListener('beforeinput', onBeforeInput, true)
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('blur', goAway)
  window.addEventListener('focus', comeBack)
  // Synchronous close + send: pagehide gives no time to await, and stop nulls sendImpl right after.
  const onPageHide = () => { closeStretch(); void flush() }
  window.addEventListener('pagehide', onPageHide)
  const timer = window.setInterval(() => { void flush() }, options.flushIntervalMs ?? DEFAULT_FLUSH_MS)

  let channel: BroadcastChannel | null = null
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(`ws-${challengeId}`)
    let reported = false
    const report = () => { if (!reported) { reported = true; push({ t: Date.now(), type: 'multi_tab' }) } }
    channel.onmessage = (m: MessageEvent<string>) => {
      if (m.data === 'hello') { channel?.postMessage('here'); report() }
      if (m.data === 'here') report()
    }
    channel.postMessage('hello')
  }

  return () => {
    const [lastSend, lastId, lastQueue] = [sendImpl, activeChallengeId, queue]
    closeStretch()
    void flush()
    // A paste right before unmount lands in a zero-delay timeout; flush again once it has. Drain this
    // challenge's own queue: the next workspace may have started (and swapped `queue`) by then.
    window.setTimeout(() => { void flush(lastSend, lastId, lastQueue) }, 0)
    window.clearInterval(timer)
    document.removeEventListener('paste', onPaste, true)
    document.removeEventListener('drop', onDrop, true)
    document.removeEventListener('copy', onCopy)
    document.removeEventListener('cut', onCopy)
    document.removeEventListener('dragstart', onCopy)
    document.removeEventListener('keydown', onKeydown, true)
    document.removeEventListener('beforeinput', onBeforeInput, true)
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('blur', goAway)
    window.removeEventListener('focus', comeBack)
    window.removeEventListener('pagehide', onPageHide)
    channel?.close()
    activeChallengeId = null
    sendImpl = null
  }
}

export const __telemetryTestHooks = {
  peekQueue: (): TelemetryEvent[] => [...queue],
  flushNow: (): Promise<void> => flush(),
}
